// Tela dedicada de detalhe de banco. Substitui o BankChooserModal antigo.
// Quando o aluno clica num banco (na lista, na home, num card de prova ativa,
// onde quer que seja), o App seta `activeBankId` e renderiza esta view dentro
// da área de "Bancos". O aluno pode ENTRAR num Estudo/Prova/Resultado, e ao
// SAIR do workspace, volta pra cá (não pra lista de bancos) — assim ele
// continua decidindo o que fazer no mesmo banco sem ping-pong.

import { signal, computed } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  ArrowLeft,
  FileText,
  BookOpen,
  PlayCircle,
  CheckCircle,
  FileDown,
  Calendar,
  Clock,
  Trash2,
  Loader2,
  AlertCircle,
  Flag,
  Pencil,
  Share2,
  Sparkles
} from "lucide-react";
import { setActiveNav } from "./App";
import { pendingNewCadernoFromBank } from "./AiStudyView";
import { enterDissertativeWorkspace } from "./DissertativeWorkspace";
import { Skeleton } from "@app/ui/components/skeleton";
import { flagEnabled } from "@app/ui/lib/feature-flags";
import { openShareDialog } from "./ShareDialog";
import { CadernoNotesPanel } from "./CadernoNotesPanel";
import { api, ApiError, type BankDetailResponse, type ProvaResumo } from "./api";
import { MarqueeOnHover } from "./Marquee";
import { enterStudyWorkspace } from "./StudyWorkspace";
import { enterProvaWorkspace } from "./ProvaWorkspace";
import { enterProvaResultView } from "./ProvaResultView";
import { openPdfExportModal } from "./PdfExportModal";
import { readBankStudyTimeMs } from "./studyTempo";
import { openAlert, openConfirm } from "./ConfirmPromptModal";

// =====================
// Estado global
// =====================

type Phase = "loading" | "error" | "ready";

/** ID do banco atualmente em detalhe. Quando setado + nav=bancos + nenhum
 *  workspace ativo, o App renderiza esta view no lugar da lista de bancos. */
export const activeBankId = signal<string | null>(null);

const phase = signal<Phase>("loading");
const detail = signal<BankDetailResponse | null>(null);
const errorMsg = signal<string | null>(null);

/** Nome do banco em detalhe (para breadcrumb). Null enquanto carrega. */
export const activeBankName = computed(() => {
  if (!activeBankId.value) return null;
  if (detail.value?.bank.id === activeBankId.value) return detail.value.bank.name;
  return null;
});

/** Abre a tela de detalhe de um banco específico. Caller é responsável por
 *  garantir activeNav === "bancos" (App.tsx faz isso). */
export function openBank(bankId: string) {
  if (activeBankId.value === bankId && detail.value) {
    // Já está aberto neste mesmo banco — não recarrega.
    return;
  }
  activeBankId.value = bankId;
  load(bankId);
}

/** Recarrega o banco atualmente em detalhe (silent — sem flicker de skeleton).
 *  Usado depois que o aluno sai de um workspace de estudo/prova e volta pro
 *  detalhe — os contadores (respondidas, acertos, tempo investido) precisam
 *  refletir o estado novo. Bug 2026-05-19: clicar no breadcrumb mostrava
 *  contadores velhos. */
export async function refreshActiveBank() {
  const id = activeBankId.value;
  if (!id) return;
  try {
    const res = await api.bankDetail(id);
    if (activeBankId.value !== id) return; // user trocou
    detail.value = res;
    // Não muda phase pra evitar piscar — quem está vendo a tela "ready"
    // continua "ready" com dados frescos.
  } catch (err) {
    // Silencioso — manter os dados velhos é melhor do que mostrar erro.
    console.warn("[bank-detail] refresh falhou:", err);
  }
}

/** Fecha a tela de detalhe (volta pra lista de bancos). */
export function closeBankDetail() {
  activeBankId.value = null;
  detail.value = null;
  errorMsg.value = null;
  phase.value = "loading";
}

async function load(bankId: string) {
  phase.value = "loading";
  detail.value = null;
  errorMsg.value = null;
  try {
    const res = await api.bankDetail(bankId);
    // Se o aluno trocou de banco enquanto carregava, descarta.
    if (activeBankId.value !== bankId) return;
    detail.value = res;
    phase.value = "ready";
  } catch (err) {
    if (activeBankId.value !== bankId) return;
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errorMsg.value = err instanceof Error ? err.message : String(err);
    phase.value = "error";
  }
}

// =====================
// Helpers de format
// =====================

const MESES = [
  "JAN", "FEV", "MAR", "ABR", "MAI", "JUN",
  "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"
];

function fmtDateTime(ts: number | null): string {
  if (ts == null) return "—";
  const d = new Date(ts);
  const dd = String(d.getDate()).padStart(2, "0");
  const mes = MESES[d.getMonth()];
  const aaaa = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${dd} ${mes} ${aaaa} · ${hh}:${mm}`;
}

function fmtDuration(ms: number): string {
  if (!ms || ms < 0) return "00:00:00";
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// =====================
// UI
// =====================

export function BankDetailView() {
  // Refaz fetch quando o bankId mudar enquanto a view continua montada
  // (clique em outro banco a partir de um deep-link, por exemplo).
  const bankId = activeBankId.value;
  useEffect(() => {
    if (bankId && detail.value?.bank.id !== bankId) {
      load(bankId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankId]);

  if (!bankId) return null;

  return (
    <div class="space-y-5">
      <BackButton />
      {phase.value === "loading" && <DetailLoading />}
      {phase.value === "error" && <DetailError />}
      {phase.value === "ready" && detail.value && (
        <DetailContent detail={detail.value} />
      )}
    </div>
  );
}

function BackButton() {
  return (
    <button
      type="button"
      onClick={closeBankDetail}
      class="inline-flex items-center gap-1.5 rounded-md border border-border bg-card/60 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:bg-accent hover:text-foreground"
    >
      <ArrowLeft className="size-3.5" />
      Voltar para bancos
    </button>
  );
}

function DetailLoading() {
  return (
    <div class="space-y-4">
      <div class="flex items-center gap-3">
        <Skeleton className="size-12 rounded-md" />
        <Skeleton className="h-6 w-72" />
      </div>
      <div class="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
      <p class="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" />
        Carregando detalhes do banco…
      </p>
    </div>
  );
}

function DetailError() {
  return (
    <div class="rounded-md border border-destructive/40 bg-destructive/5 p-6 text-center">
      <AlertCircle className="mx-auto mb-2 size-8 text-destructive" />
      <p class="text-sm text-muted-foreground">
        {errorMsg.value ?? "Não consegui carregar o banco."}
      </p>
    </div>
  );
}

function DetailContent({ detail }: { detail: BankDetailResponse }) {
  const { bank, estudo, provaAtiva, provasHistorico, lastActivityAt, dissertativaEstudo, dissertativaProva } = detail;
  // Modo do banco: "mc" (default, comportamento atual) ou "essay" (versão
  // dissertativa gerada por IA). Persistido em sessionStorage por bank.
  const hasDissertative = bank.dissertativeCount > 0;
  const modeKey = `bankMode:${bank.id}`;
  const [bankMode, setBankModeState] = useState<"mc" | "essay">(() => {
    try {
      const saved = sessionStorage.getItem(modeKey);
      return saved === "essay" && hasDissertative ? "essay" : "mc";
    } catch { return "mc"; }
  });
  function setBankMode(m: "mc" | "essay") {
    setBankModeState(m);
    try { sessionStorage.setItem(modeKey, m); } catch { /* ignore */ }
  }
  // Tempo investido (#20): soma estudo local (localStorage) + simulação ativa
  // + duração de todas as provas encerradas. Estudo é per-device porque o
  // tracker é local — fica claro pro user com "neste dispositivo" no tooltip.
  const studyMs = readBankStudyTimeMs(bank.id);
  const provaAtivaMs = provaAtiva ? provaAtiva.decorridoMs : 0;
  const provasMs = provasHistorico.reduce(
    (acc, p) => acc + (typeof p.duracaoMs === "number" ? p.duracaoMs : 0),
    0
  );
  const tempoInvestidoMs = studyMs + provaAtivaMs + provasMs;
  return (
    <>
      {/* Header */}
      <header class="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card/60 px-5 py-4">
        <div class="grid size-12 shrink-0 place-items-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20">
          <FileText className="size-6" />
        </div>
        <div class="min-w-0 flex-1">
          <h1 class="truncate text-xl font-bold tracking-tight md:text-2xl">
            {bank.name}
          </h1>
          <p class="mt-0.5 text-xs text-muted-foreground">
            {bank.questionCount} questões disponíveis
            {lastActivityAt && (
              <>
                <span class="mx-1.5 opacity-50">·</span>
                Visto por último {fmtDateTime(new Date(lastActivityAt).getTime())}
              </>
            )}
            {tempoInvestidoMs > 0 && (
              <>
                <span class="mx-1.5 opacity-50">·</span>
                <span
                  title="Soma do tempo em Modo Estudo (neste dispositivo) + simulações de prova."
                  class="inline-flex items-center gap-1"
                >
                  <Clock className="size-3" />
                  {fmtDuration(tempoInvestidoMs)} investido
                </span>
              </>
            )}
          </p>
        </div>
        {bank.isOwner && flagEnabled("student.menu.shares", false) && (
          <button
            type="button"
            onClick={() =>
              openShareDialog({
                kind: "bank",
                resourceId: bank.id,
                resourceTitle: bank.name
              })
            }
            class="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-sm font-medium text-violet-700 hover:bg-violet-500/15 dark:text-violet-300"
            title="Compartilhar este banco com outros alunos"
          >
            <Share2 className="size-3.5" />
            Compartilhar
          </button>
        )}
      </header>

      {bank.sharedByName && (
        <div class="inline-flex items-center gap-1.5 self-start rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-[11px] font-medium text-blue-700 dark:text-blue-300">
          <Share2 className="size-3" />
          Compartilhado por <strong>{bank.sharedByName}</strong> · seu
          progresso é independente
        </div>
      )}

      {bank.generatedFromCaderno && (
        <button
          type="button"
          onClick={() => {
            setActiveNav("aistudy");
            // Deeplink reverso: AiStudyView lê este signal e abre a sessão.
            void (async () => {
              const { activeSessionId } = await import("./AiStudyView");
              activeSessionId.value = bank.generatedFromCaderno!.sessionId;
            })();
          }}
          class="group inline-flex items-center gap-1.5 self-start rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-[11px] font-medium text-violet-700 transition-colors hover:bg-violet-500/20 dark:text-violet-300"
          title="Abrir o caderno que originou este simulado"
        >
          <Sparkles className="size-3" />
          Gerado por IA · a partir do caderno{" "}
          <strong class="underline-offset-2 group-hover:underline">
            {bank.generatedFromCaderno.sessionTitle}
          </strong>
          {bank.generatedFromCaderno.generatedAt && (
            <span class="opacity-70">
              · {new Date(bank.generatedFromCaderno.generatedAt).toLocaleDateString("pt-BR")}
            </span>
          )}
        </button>
      )}

      {hasDissertative && (
        <div class="rounded-md border border-fuchsia-500/30 bg-fuchsia-500/5 px-4 py-3">
          <div class="flex items-start gap-3">
            <div class="grid size-9 shrink-0 place-items-center rounded-md bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300">
              <Sparkles className="size-4" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="text-sm font-bold text-fuchsia-800 dark:text-fuchsia-200">
                Modo de questões
              </div>
              <p class="mt-0.5 text-[11px] text-muted-foreground">
                Escolha como estudar/simular esse banco. As respostas e o
                progresso de cada modo são <strong>independentes</strong>.
              </p>
              <div class="mt-2 inline-flex rounded-md border border-border bg-background p-0.5">
                <button
                  type="button"
                  onClick={() => setBankMode("mc")}
                  class={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
                    bankMode === "mc"
                      ? "bg-blue-500/15 text-blue-800 ring-1 ring-blue-500/30 dark:text-blue-200"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Múltipla escolha ({bank.questionCount})
                </button>
                <button
                  type="button"
                  onClick={() => setBankMode("essay")}
                  class={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
                    bankMode === "essay"
                      ? "bg-fuchsia-500/15 text-fuchsia-800 ring-1 ring-fuchsia-500/30 dark:text-fuchsia-200"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Dissertativa ({bank.dissertativeCount})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ações principais — variam com o bankMode (MC ou Dissertativa) */}
      <div class="space-y-3">
        {bankMode === "mc" ? (
          <ActionRow
            icon={BookOpen}
            tone="blue"
            title="Continuar Estudo"
            subtitle={
              <>
                {estudo.respondidas} de {bank.questionCount} respondidas
                {estudo.percentualAcerto != null && (
                  <>
                    <span class="mx-1.5 opacity-50">·</span>
                    {estudo.percentualAcerto}% de acerto
                  </>
                )}
                {estudo.temasFiltrados > 0 && (
                  <>
                    <span class="mx-1.5 opacity-50">·</span>
                    {estudo.temasFiltrados}{" "}
                    {estudo.temasFiltrados === 1 ? "tema" : "temas"}
                  </>
                )}
              </>
            }
            onClick={() => enterStudyWorkspace(bank.id)}
          />
        ) : (
          <ActionRow
            icon={Pencil}
            tone="violet"
            title="Estudar dissertativa"
            subtitle={
              <>
                {(dissertativaEstudo?.respondidas ?? 0)} de {bank.dissertativeCount} respondidas
                {dissertativaEstudo && dissertativaEstudo.avaliadas > 0 && (
                  <>
                    <span class="mx-1.5 opacity-50">·</span>
                    {dissertativaEstudo.avaliadas} avaliada{dissertativaEstudo.avaliadas !== 1 ? "s" : ""} pela IA
                  </>
                )}
                {dissertativaEstudo?.scoreMedia != null && (
                  <>
                    <span class="mx-1.5 opacity-50">·</span>
                    nota média {dissertativaEstudo.scoreMedia}/10
                  </>
                )}
              </>
            }
            onClick={() => enterDissertativeWorkspace(bank.id, "estudo")}
          />
        )}

        {/* Deeplink reverso: cria um caderno de IA já com este banco anexado.
            A IA vai conhecer as questões/gabaritos como referência.
            [[project-caderno-bank-context]] */}
        <ActionRow
          icon={Sparkles}
          tone="violet"
          title={bankMode === "essay" ? "Estudar dissertativa com IA" : "Estudar com IA"}
          subtitle={
            bankMode === "essay" ? (
              <>
                Caderno IA em modo discursivo
                <span class="mx-1.5 opacity-50">·</span>
                ela faz perguntas abertas e dá feedback por ponto esperado
              </>
            ) : (
              <>
                Cria um caderno com este banco como contexto da IA
                <span class="mx-1.5 opacity-50">·</span>
                ela usa enunciados e gabaritos como referência nas explicações
              </>
            )
          }
          onClick={() => {
            pendingNewCadernoFromBank.value = {
              bankId: bank.id,
              bankName: bank.name,
              bankVersion: bankMode === "essay" ? "essay" : "mc"
            };
            setActiveNav("aistudy");
          }}
        />

        {bankMode === "mc" && provaAtiva && (
          <ActionRow
            icon={PlayCircle}
            tone="amber"
            title="Retomar simulação em andamento"
            subtitle={
              <>
                Iniciada em {fmtDateTime(provaAtiva.iniciadaEm)}
                <span class="mx-1.5 opacity-50">·</span>
                {provaAtiva.respondidas}/{bank.questionCount} respondidas
                <span class="mx-1.5 opacity-50">·</span>
                {fmtDuration(provaAtiva.decorridoMs)} decorrido
              </>
            }
            onClick={() => enterProvaWorkspace(bank.id)}
          />
        )}

        {bankMode === "mc" && !provaAtiva && (
          <ActionRow
            icon={Flag}
            tone="amber"
            title="Iniciar nova simulação de prova"
            subtitle={
              <>
                Configure escopo, tempo e exibição
                <span class="mx-1.5 opacity-50">·</span>
                {bank.questionCount} questões disponíveis
              </>
            }
            onClick={() => enterProvaWorkspace(bank.id)}
          />
        )}

        {bankMode === "essay" && (
          <ActionRow
            icon={Flag}
            tone="amber"
            title={
              dissertativaProva?.finalizadaEm
                ? "Ver simulação dissertativa finalizada"
                : (dissertativaProva?.respondidas ?? 0) > 0
                  ? "Retomar simulação dissertativa"
                  : "Iniciar simulação dissertativa"
            }
            subtitle={
              <>
                {dissertativaProva?.finalizadaEm ? (
                  <>
                    Finalizada em {fmtDateTime(new Date(dissertativaProva.finalizadaEm).getTime())}
                    {dissertativaProva.scoreMedia != null && (
                      <>
                        <span class="mx-1.5 opacity-50">·</span>
                        nota média {dissertativaProva.scoreMedia}/10
                      </>
                    )}
                    <span class="mx-1.5 opacity-50">·</span>
                    gabarito liberado
                  </>
                ) : (
                  <>
                    {(dissertativaProva?.respondidas ?? 0)} de {bank.dissertativeCount} respondidas
                    <span class="mx-1.5 opacity-50">·</span>
                    gabarito SÓ aparece após finalizar
                  </>
                )}
              </>
            }
            onClick={() => enterDissertativeWorkspace(bank.id, "prova")}
          />
        )}

        <ActionRow
          icon={FileDown}
          tone="primary"
          title={bankMode === "essay" ? "Exportar dissertativa para PDF" : "Exportar para PDF"}
          subtitle={
            <>
              {bankMode === "essay"
                ? `${bank.dissertativeCount} questões + gabarito`
                : `${bank.questionCount} questões`}
              <span class="mx-1.5 opacity-50">·</span>
              imprima ou leia offline
            </>
          }
          onClick={() =>
            openPdfExportModal(bank.id, bank.name, bankMode === "essay" ? "essay" : "mc")
          }
        />
      </div>

      {/* Simulações anteriores */}
      {provasHistorico.length > 0 && (
        <section>
          <h2 class="mb-2 text-[11px] font-bold uppercase tracking-wider text-primary">
            Simulações anteriores ({provasHistorico.length})
          </h2>
          <div class="space-y-2">
            {provasHistorico.map((p) => (
              <ProvaHistoricoRow
                key={p.id ?? `${p.iniciadaEm}`}
                prova={p}
                bankId={bank.id}
              />
            ))}
          </div>
        </section>
      )}

      {/* Anotações deste banco — [[project-notes-system-caderno]] */}
      <section>
        <h2 class="mb-2 text-[11px] font-bold uppercase tracking-wider text-primary">
          Minhas anotações deste banco
        </h2>
        <div class="h-[28rem]">
          <CadernoNotesPanel targetKind="bank" targetId={bank.id} />
        </div>
      </section>
    </>
  );
}

function ActionRow({
  icon: Icon,
  tone,
  title,
  subtitle,
  onClick
}: {
  icon: typeof BookOpen;
  tone: "primary" | "amber" | "blue" | "violet";
  title: string;
  subtitle: any;
  onClick: (e: MouseEvent) => void;
}) {
  const toneStyles =
    tone === "amber"
      ? "border-amber-500/40 bg-amber-500/5 hover:border-amber-500/60 hover:bg-amber-500/10"
      : tone === "blue"
      ? "border-blue-500/40 bg-blue-500/5 hover:border-blue-500/60 hover:bg-blue-500/10"
      : tone === "violet"
      ? "border-violet-500/40 bg-violet-500/5 hover:border-violet-500/60 hover:bg-violet-500/10"
      : "border-border bg-accent/20 hover:border-primary/50 hover:bg-accent/40";
  const iconStyles =
    tone === "amber"
      ? "bg-amber-500/15 text-amber-500 ring-amber-500/30"
      : tone === "blue"
      ? "bg-blue-500/15 text-blue-500 ring-blue-500/30 dark:text-blue-400"
      : tone === "violet"
      ? "bg-violet-500/15 text-violet-500 ring-violet-500/30 dark:text-violet-400"
      : "bg-primary/10 text-primary ring-primary/20";
  return (
    <button
      type="button"
      onClick={onClick}
      class={`group flex w-full items-center gap-4 rounded-md border px-4 py-3 text-left transition-colors ${toneStyles}`}
    >
      <div
        class={`grid size-11 shrink-0 place-items-center rounded-md ring-1 ${iconStyles}`}
      >
        <Icon className="size-5" />
      </div>
      <div class="min-w-0 flex-1">
        <div class="text-sm font-bold">{title}</div>
        <MarqueeOnHover className="mt-0.5 text-xs text-muted-foreground">
          {subtitle}
        </MarqueeOnHover>
      </div>
      <div class="text-muted-foreground/60 transition-transform group-hover:translate-x-0.5">
        ›
      </div>
    </button>
  );
}

function ProvaHistoricoRow({
  prova,
  bankId
}: {
  prova: ProvaResumo;
  bankId: string;
}) {
  const [deleting, setDeleting] = useState(false);
  async function onDelete() {
    if (!prova.id) return;
    const ok = await openConfirm({
      title: "Excluir esse resultado?",
      message:
        "O resultado vai sumir do seu histórico e da home. O professor/admin ainda consegue auditar a tentativa se necessário. Você pode iniciar uma nova simulação a qualquer momento.",
      confirmLabel: "Excluir",
      danger: true
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await api.hideProva(bankId, prova.id);
      await refreshActiveBank();
    } catch (err) {
      void openAlert({
        title: "Erro",
        message:
          err instanceof Error
            ? `Não consegui excluir: ${err.message}`
            : "Não consegui excluir o resultado."
      });
    } finally {
      setDeleting(false);
    }
  }
  return (
    <div class="rounded-md border border-border bg-accent/10 px-4 py-3">
      <div class="flex items-center gap-2 text-sm font-bold">
        <Calendar width="1.3em" height="1.3em" className="text-muted-foreground" />
        {fmtDateTime(prova.iniciadaEm)}
        <button
          type="button"
          title="Excluir esse resultado"
          aria-label="Excluir resultado"
          disabled={deleting || !prova.id}
          onClick={() => void onDelete()}
          class="ml-auto grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
        >
          {deleting ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
        </button>
      </div>
      <div class="mt-1.5 flex flex-wrap items-center gap-x-2 text-xs">
        <span class="font-semibold">
          {prova.respondidas}/{prova.total || prova.respondidas} respondidas
        </span>
        <span class="opacity-50">·</span>
        <span class="font-semibold text-primary">
          {prova.percentualAcerto ?? 0}% de acerto
        </span>
        <span class="opacity-50">·</span>
        <Clock width="1.3em" height="1.3em" className="text-muted-foreground" />
        <span class="text-muted-foreground">{fmtDuration(prova.duracaoMs)}</span>
      </div>
      <div class="mt-2.5">
        <button
          type="button"
          onClick={() => {
            if (!prova.id) return;
            enterProvaResultView(bankId, prova.id);
          }}
          class="flex w-full items-center justify-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent"
        >
          <CheckCircle width="1.3em" height="1.3em" />
          Ver resultado
        </button>
      </div>
    </div>
  );
}
